import { useState } from "react";
import { LineChart, Send, Loader2, AlertTriangle, Users } from "lucide-react";
import { apiFetch } from "@/portal/api";
import { fmtCZK } from "@/portal/components/adminShared";
import { toast } from "@/components/ui/sonner";
import { confirmDialog } from "@/portal/confirm";

const monthKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;

const FinanceOverviewCard = ({ invoices, token, onGoClients }) => {
  const [sending, setSending] = useState(null); // "unpaid" | "overdue"
  const today = new Date().toISOString().slice(0, 10);
  const rent = invoices.filter((i) => i.invoice_type !== "deposit" && i.status !== "cancelled");
  const isUnpaid = (i) => i.status !== "paid" && i.status !== "cancelled";
  const isOverdue = (i) => isUnpaid(i) && (i.status === "overdue" || (i.due_on && i.due_on < today));
  const unpaidAll = invoices.filter(isUnpaid);
  const overdueAll = invoices.filter(isOverdue);

  const now = new Date();
  const months = [];
  for (let k = 11; k >= 0; k--) {
    const d = new Date(now.getFullYear(), now.getMonth() - k, 1);
    months.push({ key: monthKey(d), label: d.toLocaleDateString("cs-CZ", { month: "short" }), year: d.getFullYear() });
  }
  const sum = (arr) => arr.reduce((s, i) => s + (Number(i.amount) || 0), 0);
  const series = months.map((m) => ({
    ...m,
    issued: sum(rent.filter((i) => (i.issued_on || i.created_at || "").slice(0, 7) === m.key)),
    paid: sum(rent.filter((i) => i.status === "paid" && (i.paid_on || "").slice(0, 7) === m.key)),
  }));
  const maxV = Math.max(1, ...series.flatMap((s) => [s.issued, s.paid]));
  const h = (v) => `${Math.max(v > 0 ? 3 : 0, Math.round((v / maxV) * 100))}%`;
  const issued12 = sum(series.map((s) => ({ amount: s.issued })));
  const paid12 = sum(series.map((s) => ({ amount: s.paid })));

  const debtors = Object.values(unpaidAll.reduce((acc, i) => {
    const k = i.client_id || i.clientName;
    acc[k] = acc[k] || { key: k, name: i.clientName, count: 0, total: 0, overdue: 0, oldest: null };
    acc[k].count += 1;
    acc[k].total += Number(i.amount) || 0;
    if (isOverdue(i)) acc[k].overdue += Number(i.amount) || 0;
    if (i.due_on && (!acc[k].oldest || i.due_on < acc[k].oldest)) acc[k].oldest = i.due_on;
    return acc;
  }, {})).sort((a, b) => b.overdue - a.overdue || b.total - a.total);

  const bulkSend = async (scope) => {
    const list = scope === "overdue" ? overdueAll : unpaidAll;
    if (!list.length) { toast.info("Není co odeslat."); return; }
    const ok = await confirmDialog({
      title: scope === "overdue" ? `Odeslat ${list.length} faktur po splatnosti?` : `Odeslat ${list.length} nezaplacených faktur?`,
      description: "Každému klientovi přijde e-mail s PDF jeho faktury (a platebními údaji). Odeslání může chvíli trvat.",
      confirmText: "Odeslat",
    });
    if (!ok) return;
    setSending(scope);
    try {
      const r = await apiFetch(`/admin/invoices/bulk-email?scope=${scope}`, { method: "POST", token });
      toast.success(`Odesláno ${r.sent} z ${r.total} faktur${r.no_email ? `, bez e-mailu: ${r.no_email}` : ""}${r.failed ? `, chyb: ${r.failed}` : ""}.`);
    } catch (e) { toast.error(e.message); } finally { setSending(null); }
  };

  const fmtD = (s) => (s ? new Date(s + "T00:00:00").toLocaleDateString("cs-CZ", { day: "numeric", month: "numeric" }) : "—");

  return (
    <div className="mt-6 glass rounded-2xl p-6" data-testid="finance-overview-card">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-1">
        <h3 className="font-['Cabinet_Grotesk'] text-lg font-bold flex items-center gap-2">
          <LineChart className="w-5 h-5 text-orange-400" /> Přehled financí – nájemné
        </h3>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => bulkSend("unpaid")} disabled={!!sending || !unpaidAll.length}
            className="text-xs font-medium px-3 py-1.5 rounded-full bg-orange-500/20 text-orange-300 hover:bg-orange-500/30 inline-flex items-center gap-1.5 disabled:opacity-50"
            data-testid="bulk-send-unpaid-btn" title="Pošle všem klientům jejich nezaplacené faktury e-mailem">
            {sending === "unpaid" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            Poslat nezaplacené ({unpaidAll.length})
          </button>
          <button onClick={() => bulkSend("overdue")} disabled={!!sending || !overdueAll.length}
            className="text-xs font-medium px-3 py-1.5 rounded-full bg-red-500/20 text-red-300 hover:bg-red-500/30 inline-flex items-center gap-1.5 disabled:opacity-50"
            data-testid="bulk-send-overdue-btn" title="Pošle jen faktury po splatnosti">
            {sending === "overdue" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <AlertTriangle className="w-3.5 h-3.5" />}
            Poslat po splatnosti ({overdueAll.length})
          </button>
        </div>
      </div>
      <p className="text-xs text-zinc-500 mb-5">Posledních 12 měsíců: vystavené nájemné (podle data vystavení) vs. skutečně zaplacené (podle data úhrady). Kauce se nepočítají.</p>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3">
          <div className="flex items-center gap-4 text-[11px] text-zinc-500 mb-3">
            <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-zinc-500/70" /> vystaveno · {fmtCZK(issued12)}</span>
            <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-orange-500" /> zaplaceno · {fmtCZK(paid12)}</span>
          </div>
          {issued12 === 0 && paid12 === 0 ? (
            <p className="text-sm text-zinc-500 py-8 text-center">Zatím žádné faktury za nájemné.</p>
          ) : (
            <div className="flex items-end gap-1.5 h-40" data-testid="finance-chart">
              {series.map((s) => (
                <div key={s.key} className="flex-1 flex flex-col items-center gap-1 h-full" data-testid={`finance-month-${s.key}`}
                  title={`${s.label} ${s.year}: vystaveno ${fmtCZK(s.issued)}, zaplaceno ${fmtCZK(s.paid)}`}>
                  <div className="flex-1 w-full flex items-end justify-center gap-0.5">
                    <div className="w-[40%] rounded-t-sm bg-zinc-500/50" style={{ height: h(s.issued) }} />
                    <div className="w-[40%] rounded-t-sm bg-orange-500/90" style={{ height: h(s.paid) }} />
                  </div>
                  <span className="text-[10px] text-zinc-500 leading-none">{s.label}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="lg:col-span-2" data-testid="debtors-list">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-zinc-300 inline-flex items-center gap-1.5"><Users className="w-4 h-4 text-red-400" /> Dlužníci</h4>
            <span className="text-xs text-zinc-500">{fmtCZK(sum(unpaidAll))} nezaplaceno</span>
          </div>
          {debtors.length === 0 ? (
            <p className="text-sm text-zinc-500 py-6 text-center">Nikdo nedluží 🎉</p>
          ) : (
            <div className="space-y-1.5 max-h-44 overflow-y-auto pr-1">
              {debtors.map((d) => (
                <div key={d.key} className="flex items-center justify-between gap-2 text-sm rounded-lg bg-white/5 px-3 py-2" data-testid={`debtor-${d.key}`}>
                  <div className="min-w-0">
                    <p className="text-zinc-200 truncate">{d.name}</p>
                    <p className="text-[11px] text-zinc-500">{d.count}× faktura · nejstarší spl. {fmtD(d.oldest)}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className={`font-medium ${d.overdue > 0 ? "text-red-400" : "text-amber-300"}`}>{fmtCZK(d.total)}</p>
                    {d.overdue > 0 && <p className="text-[11px] text-red-400/80">po splatnosti {fmtCZK(d.overdue)}</p>}
                  </div>
                </div>
              ))}
            </div>
          )}
          {debtors.length > 0 && (
            <button onClick={onGoClients} className="mt-2 text-xs text-orange-300 hover:text-orange-200" data-testid="debtors-go-clients">Otevřít klienty →</button>
          )}
        </div>
      </div>
    </div>
  );
};

export default FinanceOverviewCard;
